import { Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import Box from 'src/components/MuiBox';
import Grid from 'src/components/MuiGrid';
import { mainContentWrapperCreateDeviceStyles, createDeviceDatepickerPopperStyles } from 'src/styles/page';
import { submitButtonStyles } from 'src/styles/form';
import { createShouldForwardProp } from 'src/theme/styledCustomProps';

/**
 * 페이지 최상단 래퍼 정본 — `.frame{padding:18px}` 골격만 담당.
 * 각 페이지는 `styled(PageMainWrapper)(...)`로 확장해 padding·title_divider·고유 셀렉터를 얹는다.
 * 이 골격을 바꾸려면 이 컴포넌트 하나만 수정하면 모든 확장체에 반영된다.
 */
export const PageMainWrapper = styled(Box)(
  () => `
  .frame {
    padding : 18px;
  }
`,
);

/** `.title_divider{margin-top:18px}` 포함 변형 — PageMainWrapper 확장 */
export const PageMainWrapperWithTitleDivider = styled(PageMainWrapper)(
  () => `
  .title_divider {
    margin-top : 18px;
  }
`,
);

/**
 * padding:10px 단일 변형 — InspectionList/MaintenanceList/ManufacturerList/ProductList 4곳 바이트 동일.
 * 이 4곳은 이 정본을 그대로 alias한다.
 */
export const PageMainWrapperPadding10px = styled(PageMainWrapper)(
  () => `padding : 10px;`,
);

/** padding:10px + title_divider 단일 변형 — setting/Theme가 그대로 alias */
export const PageMainWrapperWithTitleDividerPadding10px = styled(PageMainWrapperWithTitleDivider)(
  () => `padding : 10px;`,
);

/** padding:0px 10px 10px 10px 단일 변형 — support/setting-CreateUserModal 바이트 동일. 이 2곳은 이 정본을 그대로 alias한다. */
export const PageMainWrapperPadding0_10_10_10 = styled(PageMainWrapper)(
  () => `padding : 0px 10px 10px 10px;`,
);

/**
 * ContentWrapper(Paper) 골격 정본 — box-shadow 제거 + border-radius 10px + 959px 이하 padding 18px
 * + `&.with_button{padding-bottom:0px}`.
 */
export const PageContentWrapper = styled(Paper)(
  () => `
  width: 100%;
  box-shadow : none;
  border-radius: 10px;
  @media (max-width : 959px){
    padding : 18px
  }
  &.with_button {
    padding-bottom : 0px;
  }
`,
);

/**
 * PageContentWrapper + submitButtonStyles({withDelete:false}) 확장 — Admin layout Info 3형제
 * (DetailLayoutInfo/ParameterInfo/ProfileInfo) 바이트 동일. 이 정본을 그대로 alias.
 */
export const PageContentWrapperNoDelete = styled(PageContentWrapper)(
  () => submitButtonStyles({ withDelete: false }),
);

/**
 * CreateDeviceModal 계열 MainContentWrapper 골격 정본(base) — src/styles/page.ts의
 * mainContentWrapperCreateDeviceStyles(.company_location_checkbox{margin-top:10px} 포함)를 그대로 담은
 * styled 컴포넌트. 페이지별 변형은 이 base를 확장(styled(Base))해 margin-top/bottom·추가 블록만 얹는다.
 */
export const CreateDeviceMainContentWrapperBase = styled(Box)(
  ({ theme }: any) => mainContentWrapperCreateDeviceStyles(theme),
);

/**
 * base + .company_location_checkbox{margin-bottom:-16px}(margin-top은 base 10px 유지) — 완전 동일
 * 6곳(DeviceList/mes-Stock/Admin 3형제/DataLabelSetting)이 이 정본을 그대로 alias.
 */
export const CreateDeviceMainContentWrapper = styled(CreateDeviceMainContentWrapperBase)(
  () => `
  .company_location_checkbox {
    margin-bottom: -16px;
  }
  `,
);

/**
 * base + .company_location_checkbox{margin-top:0.35rem}(뒤 선언이 base의 10px을 override) —
 * location/ProductList/CreateUserModal 3곳 바이트 동일. 이 3곳은 이 정본을 그대로 alias.
 */
export const CreateDeviceMainContentWrapperMarginTop = styled(CreateDeviceMainContentWrapperBase)(
  () => `
  .company_location_checkbox {
    margin-top: 0.35rem;
  }
  `,
);

/**
 * CreateDeviceMainContentWrapperMarginTop + datepicker popper 확장 —
 * InspectionDetail/InspectionList 2곳 바이트 동일. 이 정본을 그대로 alias.
 */
export const CreateDeviceMainContentWrapperWithDatepickerPopper = styled(CreateDeviceMainContentWrapperMarginTop)(
  ({ theme }: any) => createDeviceDatepickerPopperStyles(theme),
);

/**
 * useCustomPadding prop 기반 padding 전환 Grid 정본 팩토리 — monitoring/DeviceDetail↔
 * Dashboard/UnifiedAlertLogTable 2곳 구조 바이트 동일, `useCustomPadding=false`일 때의
 * padding 값만 페이지마다 다름(DeviceDetail:'0px 10px 10px 10px', UnifiedAlertLogTable:'0px 18px 18px 18px').
 * 각 사이트에서 해당 값을 인자로 고정한 컴포넌트를 만들어 alias한다.
 */
export const agGridPageGridStyles = (defaultPadding: string) => styled(Grid, {
  shouldForwardProp: createShouldForwardProp('useCustomPadding'),
})(
  ({ useCustomPadding }: any) => `
  padding: ${useCustomPadding ? '0px 0px 0px 0px' : defaultPadding};
  `,
);
